/**
 * The application main configuration - often called server config.
 * It contains some common AppFrame specific configuration parameter (like OIDC configuration, theme, cookie banner settings, etc).
 *
 * The URL of the main configuration is taken from the `main-config` meta tag in `index.html`, and it is loaded at AppFrame startup.
 * It's default build value can be specified `REACT_APP_MAIN_CONFIG` environment variable. If nothing is set `conf/server-config.json` is used.
 *
 * The main configuration must imply with the {@link MainConfig | Main Configuration Interface}.
 *
 * The interface can be extended with additional properties with TypeScript interface overrides.
 *
 * @module Configurations > Main Configuration
 */

import { HTMLAttributeReferrerPolicy } from 'react';

import { AuthManagerConfig, AuthMockUserProfile } from 'types/services/appframe/auth';

import { LogLevel, SingleAppMode, WithAvailable, WithNavigationArea } from '../../common/base';
import { AboutItem } from '../../services/appframe/about';
import { PlatformConfigs } from '../../services/appframe/platform';
import { PreferencesMenuItemConfig } from '../../services/appframe/preferences';
import { StorageConfig } from '../../services/appframe/storage';
import { Timezone } from '../../services/appframe/timezone';
import { CollectionConfig } from '../../services/base';

import { AppFrameTheme } from './theme/appFrame';

/**
 * Product menu item configuration object.
 */
export interface ProductsMenuConfig extends CollectionConfig, WithAvailable {
  /**
   * The product name which is displayed as a menu text
   */
  text: string;
  /**
   * The URL where the browser is redirected when the user clicks on the product menu item.
   */
  url: string;
  /**
   * The referrer policy to passed to the opened window.
   */
  referrerPolicy?: HTMLAttributeReferrerPolicy;
  /**
   * The target of the opened window. See the HTML Anchor element documentation for details.
   */
  target: '_blank' | '_self' | string;
  /**
   * The category name where the product menu item will be placed (grouping).
   * When `category` is no set it will be at the top of the categories list without grouping.
   */
  category?: string;
  /**
   * The menu item icon string or URL. See icons/icons.ts for available icons!
   */
  icon?: string;
  /**
   * Tooltip text displyed on the icon.
   */
  iconAltText?: string;
}

/**
 * Profile menu item configuration object.
 */
export interface ProfileMenuItemConfig extends CollectionConfig, WithNavigationArea, WithAvailable {
  /**
   * The unique identifier of the menu item.
   */
  id?: string;

  /**
   * The order of the menu item.
   */
  order?: number;

  /**
   * The text of the menu item or a translation key in the appframe.
   */
  text: string;

  /**
   * A material ui icon name or an url to an icon resource.
   * By default AppFrame tries to map to an exiting icon in `icons/icons.ts`, but if it is not
   * found it is tried to download as an resource.
   */
  icon?: string;
}

/**
 * Cookie item used in the cookie property
 */
export interface CookieItem {
  /**
   * Type and id of the allowed cookie
   */
  id: 'necessary' | 'preferences' | 'statistics' | 'marketing';

  /**
   * Determines that the checkbox is modifiable or not
   */
  readOnly: boolean;

  /**
   * The default state of the checkbox
   */
  checked: boolean;
}

/**
 * The available environment modes.
 */
export enum EnvMode {
  Dev = 'dev',
  Test = 'test',
  Int = 'int',
  Prod = 'prod',
  Custom = 'custom',
}

/**
 * Configuration options for the environment indicator.
 */
export interface EnvConfig {
  /**
   * The text displayed in the environment indicator.
   * This can be used to show the current environment (e.g., "Production", "Development").
   * If not set, the default title will be used which is the same as the `mode`.
   */
  title?: string;

  /**
   * Specifies the mode of the environment.
   * @default 'prod'
   */
  mode?: EnvMode;

  /**
   * A custom icon to represent the environment.
   * This will only be used if the `mode` is set to `'custom'`.
   * Only icon names exported from "src/icons" folder can be used!
   */
  customIcon?: string;

  /**
   * The application version displayed in the environment indicator.
   */
  version?: string;

  /**
   * A flag indicating whether the environment indicator is allowed.
   * @default: `true` except for the `prod` mode.
   */
  allowEnvIndicator?: boolean;
}

/**
 * Additional options to set the different titles in the application
 */
export interface TitleOptions {
  /**
   * The url of the favicon
   */
  faviconUrl?: string;

  /**
   * Can override the application title to show an svg istead of text
   */
  titleSvgUrl?: string;

  /**
   * titleSvgUrl is set this string applies as a tooltip. If unset the application title is applied as tooltip
   */
  titleSvgTooltip?: string;

  /**
   * If tabTitle is set it replaces the tab title.
   * If tabTitle is not added then the title is applied to the tab title and the application title.
   */
  tabTitle?: string;
}

/**
 * Base configuration for help services which defines an initial application help context.
 */
export interface HelpConfig {
  /**
   * The URL which contains the help topics. It is called when help service is initialized.
   *
   * Note: the downloading help topics from an authorized service is not supported!
   */
  allTopicsUrl: string;

  /**
   * The URL where help topics can be loaded from.
   *
   * The URL can have the following substitution path variables:
   * a  context:
   *  - {{lang}}: substitutes the actually selected language in the URL
   *  - {{file}}: substitutes the file name of the help topic
   *
   * For example when the language is set to `en` and the help topic
   * file name is `my-topic.html` the URL `https://example.com/resources/help/{{lang}}/{{file}}`
   * is replaced to `https://example.com/resources/help/en/my-topic.html`.
   */
  url: string;

  /**
   * The list of default topics path which are always displayed.
   *
   * The help menu will display these topics even if they are not a context is set, or
   * at the top level of the help menu when a context is set.
   *
   * The help topics are dot separated paths in the `allTopics` variable.
   *
   * If not specified only the context sensitive part of the help menu is displayed.
   */
  defaultTopics?: string[];
}

/**
 * Defines the available titleLevel options
 */
export type TitleLevelOptions = 'netline' | 'simple' | 'domain' | 'application';

/**
 * About dialog configuration options
 */
export interface AboutConfig {
  /**
   * Defines static module metaData to about dialog.
   * In plugin modules the preferred sway to register module metadata is through the services.appframe.about.addModule() method.
   */
  moduleVersions?: AboutItem[];

  /**
   * Adds the url to About dialog's license click handler.
   * LicenseUrl defined in a main module overrides the one defined in aboutConfig.
   */
  licenseUrl?: string;

  /**
   * The type of the title in the About dialog.
   *
   * The about title is always displayed with Lufthansa font at the top of the dialog.
   *
   * There are three title levels available regarding how the title is rendered:
   * - simple: always displays the text in `baseTitle` (by default 'NetLine') independently how the module is defined;
   * - netline: it is the deprecated version of `simple';
   * - domain: displays the 'NetLine' text in the title and next to it the domainName with a lighter color.
   * If domainName is undefined it falls back to `baseTitle` (eg. 'NetLine')
   * - application: the title will be taken completely from the modules `title` property.
   *
   * Note: the value of this item is case-insensitive!
   *
   * @default 'simple'
   */
  titleLevel?: TitleLevelOptions;

  /**
   * The title which appears before `domainName` when `titleLevel` is 'simple' or 'domain'.
   */
  baseTitle?: string;

  /**
   * The name of the domain which appears in the title when titleLevel is 'domain'.
   * examples: Flight Scheduling, Ground Operation, Crew Management.
   */
  domainName?: string;

  /**
   * The url of the airline logo. It will be rendered in the header of the About dialog.
   * if not set the default Lufthansa logo is rendered.
   */
  airlineLogoUrl?: string;

  /**
   * The url of the airline tail logo. It will be rendered in the bottom right corner of the About dialog.
   * if not set the default Lufthansa tail logo is rendered.
   */
  tailLogoUrl?: string;
}

/**
 * Contains the authorization (OIDC) information and some common configuration.
 */
export interface MainConfig {
  /**
   * Contains the theme object for appFrame configurations.
   * Default values can be found in `utils/theme.ts`.
   * The strings are colors, which can be hex, rgb or string like 'black'.
   * If a property is missing the default value will be used.
   */
  theme?: AppFrameTheme;

  /**
   * When it is set to `true` platform will be calculated only at load time, so
   * AppFrame and application modules / widgets won't be notified about the platform
   * change (via `appFrame.platform.onPlatformChange` service).
   *
   * @default false
   */
  disablePlatformChange?: boolean;

  /**
   * Logging details of AppFrame and oidc module.
   */
  logLevel?: LogLevel;

  /**
   * Describes the OIDC authentication endpoint.
   * It has two manadatory parameters which are authentication endpoint (`authority`)
   * and the OIDC client identifier (`clientId`).
   * If `oidc` is set to `null` (or not specified) no authentication will happen.
   * In this case some authentication mock data can be set by specifying the `mockUser` property.
   * This can be used for early project phase.
   */
  oidc?: AuthManagerConfig | null;

  /**
   * A mock user in case no `oidc` authentication is provided.
   *
   * @deprecated Please use `oidc.mockUser` instead.
   * @see {@link AuthManagerConfig.mockUser}
   */
  mockUser?: AuthMockUserProfile;

  /**
   * Whether to force to display the AppFrame in Single App Mode or automatically decide based on sidebars items.
   * In Single App Mode the desktop version does not display the sidebar navigation area and the whole
   * client area below the AppFrame header belongs to applications.
   *
   * When it is not set AppFrame always displays the sidebar navigation.
   *
   * @default undefined
   */
  singleAppMode?: SingleAppMode;

  /**
   * If it is set to `true` the sidebar will not be visible and the page will be routed to the default application
   * module. When no default application is loaded, the first application will be displayed which the user has the
   * permission. This parameter is optional, by default the sidebar is visible.
   *
   * @default true
   * @deprecated
   */
  singleAppOnly?: boolean;

  /**
   * By default submenu items are displayed in an opening sidebar menu.
   * Set this parameter to `true` to always dispaly submenu items in a popup menu.
   *
   * @default false
   */
  displaySubmenuInPopup?: boolean;

  /**
   * An optional string which is displayed after in the header after the application / actually displayed module title.
   * It is useful to automatically display customer info or differentiate TEST, PROD, DEV environments.
   */
  env?: string | EnvConfig;

  /**
   * If it's set to `false` then the theme selector won't be renderer.
   * If it's set to `true` the theme selector will be rendered.
   * The `darkMode` property will be passed down to the modules.
   * For more info on the property check under Application Module (Module) Interface.
   *
   * @default false
   */
  allowDarkMode?: boolean;

  /**
   * If it's set to `false` then the time zone selector won't be renderer.
   * If it's set to `true` the time zone selector will be rendered.
   * The time zone mode can be Utc .
   * For more info on the property check under Application Module (Module) Interface.
   *
   * @default false
   */
  allowTimezone?: boolean;

  /**
   * The default time zone mode to be used. If not set the UTC_WITH_LOCAL value will be used.
   *
   * @default 'utc_with_local'
   */
  defaultTimezone?: Timezone;

  /**
   * If it's not defined the language selector won't be rendered in the appframe.
   * If it's set to an array of strings (for example: ["en", "de", "cn"]) then the language
   * selector will be renderer with the specified languages.
   * If you add a new language please check if appframe contains the given languages translation file
   * and if flags.js contains the flag. If not you will have to add it.
   *
   * @default ['en']
   */
  languages?: string[];

  /**
   * The default language to be used when there are no language set or
   * the application does not (longer) support the saved language.
   *
   * @default 'en'
   */
  defaultLanguage?: string;

  /**
   * Additional preferences menu items can be defined here.
   * Preferences menu can be extended from server-config.json and dynamically through services.
   * If preferences menu is empty the gear icon is hidden.
   * Menu items are ordered ascending by the 'order' property.
   */
  preferencesMenu?: PreferencesMenuItemConfig[];

  /**
   * Additional profile menu items can be defined here.
   * Clicking on the menu item will trigger a window.postMessage event which you can listen to in the modules.
   */
  profileMenu?: ProfileMenuItemConfig[];

  /**
   * Products menu can be extended from server-config.json and dynamically through services.
   * If products menu is empty the hamburger menu icon is hidden.
   * Products are ordered ascending by the 'order' property.
   */
  products?: ProductsMenuConfig[];

  /**
   * Products can be categorized.
   * if false: categories are disabled
   * if string: the defined string will be the default category, products defined without category property
   * will get this category
   *
   * @default 'Apps'
   */
  defaultProductCategory?: false | string;

  /**
   * Contains the preferred property/properties of the user profile to show as name.
   *
   * @default 'preferred_name'.
   */
  displayUserName?: string | string[];

  /**
   * The application and tab title
   *
   * Application title can be set in two ways:
   * - statically: from main configuration {@link title} and {@link titleOptions} prop
   * - dynamically: by using the
   * {@link "Services > Feature Services > Title Services" >.TitleServices.setTitle | TitleServices.setTitle}
   * service method.
   */
  title?: string;

  /**
   * Additional options to set the faviconUrl, tabTitle and change the appTitle to an svg
   */
  titleOptions?: TitleOptions;

  /**
   * It accepts a file name or an absolute url. In the appframe public folder there is a folder named logo.
   * You have to put the image which you want to use here.
   * In server-config.json you have to only specify the name of the file, the path already points to this folder.
   */
  logoUrl?: string;

  /**
   * If provided the cookie banner (netline-ui CookieBanner component) will appear with
   * the options this array provides.
   */
  cookie?: CookieItem[];

  /**
   * The url for the cookie policy. If not provided and cookies are  provided (enabled),
   * the cookie policy link will not be rendered.
   */
  cookiePolicyUrl?: string;

  /**
   * Here you can override default platformConfigs.
   */
  platforms?: PlatformConfigs;

  /**
   * Base configuration for help services
   */
  help?: HelpConfig;

  /**
   * Specify storage options for the default storage.
   *
   * If it is not specified the default storage only uses `localStorage` and it need to be reconfigured with
   * the `services.appFrame.storage.reconfigure(...)` call.
   */
  storage?: StorageConfig;

  /**
   * Specify user storage options for the default user storage.
   */
  userStorage?: StorageConfig;

  /**
   * Provides optional settings for About dialog.
   */
  about?: AboutConfig;

  /**
   * If it is set to `true` the sidebar can be reordered by the user.
   * @default false
   */
  allowSidebarReorder?: boolean;

  /**
   * If it is enabled a WebLock will be enabled not to allow to put AppFrame's browser tab into sleep mode.
   * By default it is turned on, so set it to `true` if some abnormality is experienced.
   *
   * Please note that this is an experimental feature and must be used carefully.
   *
   * @experimental
   * @default true
   */
  allowWebLock?: boolean;

  /**
   * Enables the application to toggle fullscreen mode when set to `true`.
   * @default true
   */
  allowFullscreen?: boolean;

  /**
   * The pinning mode of the sidebar. It can be specified how sidebar should behave for different
   * menu structure setup.
   *
   * In legacy way, when a sidebar item is clicked and the sidebar item has submenu, or when
   * the sidebar is opened with the toggle button, the sidebar panel appeared on over the main view.
   * In this case, when the user clicks on the main view the sidebar is closed.
   *
   * In case of sidebar pinning when the user clicks on the open button directly, the sidebar is
   * opened on the left side of the screen and pushes the main view to the right side.
   * When the the user clicks on a menu item which has a submenu, or on the customize button, the sidebar
   * opens over the main view and the main view is not pushed to the right side.
   *
   * This setting is mainly for compatibility with older versions of AppFrame.
   *
   * It can have the following values:
   * - `'auto'`: when the open button is pressed, the sidebar is pinned only when there are no any items
   * with submenus on the sidebar. Otherwise it is opened over the main view.
   * - `'always'`: when the open button is pressed, the sidebar is always pinned to the left side of the
   * screen no matter if it has an item with submenu or not.
   * - `'never'`: when the open button is pressed, no pinning occurs and the sidebar always opened over
   * the main view.
   *
   * @default 'always'
   */
  sidebarPinningMode?: 'auto' | 'always' | 'never';
}
